import { useState, useMemo, useEffect } from "react";
import { BREED_PALS, ELEMENT_META, WORK_META } from "@/data/breedingPals";
import { PalAvatar, ElementBadges } from "@/components/PalAvatar";
import { usePalDetail } from "@/components/PalDetailModal";
import { loadOwned } from "@/lib/breeding";
import { api } from "@/lib/api";
import { Search, Star, Flame, Droplets, Sprout, Zap, Hammer, Hand, Axe, Pickaxe, Pill, Snowflake, Truck, Wheat, Package, X, SlidersHorizontal } from "lucide-react";

const ELEMENTS = ["neutral", "fire", "water", "electric", "grass", "ground", "ice", "dark", "dragon"];

// Work suitabilities in a fixed, readable order.
const WORK_ORDER = ["kindling", "watering", "planting", "electricity", "handiwork", "gathering", "lumbering", "mining", "medicine", "cooling", "transport", "farming"];
const WORK_ICONS = {
  kindling: Flame, watering: Droplets, planting: Sprout, electricity: Zap,
  handiwork: Hammer, gathering: Hand, lumbering: Axe, mining: Pickaxe,
  medicine: Pill, cooling: Snowflake, transport: Truck, farming: Wheat,
};
const WORK_COLORS = {
  kindling: "#F97316", watering: "#38BDF8", planting: "#22C55E", electricity: "#FACC15",
  handiwork: "#F59E0B", gathering: "#84CC16", lumbering: "#A16207", mining: "#94A3B8",
  medicine: "#EC4899", cooling: "#67E8F9", transport: "#C084FC", farming: "#A3E635",
};

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const starsOf = (r) => Math.max(1, Math.min(5, Math.ceil((r || 0) / 2)));

export default function Pals() {
  const openPal = usePalDetail();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(null);
  const [works, setWorks] = useState([]); // selected work skills
  const [matchAll, setMatchAll] = useState(false); // false = au moins une, true = toutes
  const [minLevel, setMinLevel] = useState(1);
  const [collectionOnly, setCollectionOnly] = useState(false);
  const [owned, setOwned] = useState(() => loadOwned());

  useEffect(() => {
    api.get("/breeding/owned").then(({ data }) => setOwned(new Set(data.owned || []))).catch(() => {});
  }, []);

  const toggleWork = (k) => setWorks((w) => (w.includes(k) ? w.filter((x) => x !== k) : [...w, k]));

  const list = useMemo(() => {
    const term = norm(q).trim();
    return BREED_PALS.filter((p) => {
      if (term && !(norm(p.fr).includes(term) || norm(p.name).includes(term))) return false;
      if (filter && !(p.elements || []).includes(filter)) return false;
      if (collectionOnly && !owned.has(p.i)) return false;
      if (works.length) {
        const w = p.d?.work || {};
        const ok = works.map((k) => (w[k] || 0) >= minLevel);
        if (matchAll ? ok.some((x) => !x) : !ok.some((x) => x)) return false;
      }
      return true;
    });
  }, [q, filter, works, matchAll, minLevel, collectionOnly, owned]);

  const activeFilters = works.length > 0 || collectionOnly || !!filter;
  const clearAll = () => { setWorks([]); setFilter(null); setCollectionOnly(false); setMinLevel(1); setQ(""); };

  return (
    <div className="fade-up space-y-5">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Paldeck</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Base de données des Pals</h1>
        <p className="text-sm text-muted-foreground mt-1">{list.length} Pals · stats, éléments, aptitudes de travail et capacité partenaire</p>
      </div>

      {/* Search + elements */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="pal-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un Pal…"
            className="w-full rounded-md bg-[hsl(var(--input))] border border-border pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="filter-element-all" onClick={() => setFilter(null)} className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${!filter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>Tous</button>
          {ELEMENTS.map((el) => {
            const meta = ELEMENT_META[el] || {};
            const active = filter === el;
            return (
              <button key={el} data-testid={`filter-element-${el}`} onClick={() => setFilter(active ? null : el)} className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors" style={{ color: active ? "#0b0f19" : meta.color, backgroundColor: active ? meta.color : `${meta.color}14`, borderColor: `${meta.color}66` }}>
                {meta.label || el}
              </button>
            );
          })}
        </div>
      </div>

      {/* Work-suitability filter panel */}
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-3 sm:p-4 space-y-3" data-testid="work-filter-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground inline-flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5 text-primary" /> Aptitudes de travail</span>
          <button data-testid="collection-only-toggle" onClick={() => setCollectionOnly((v) => !v)} className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${collectionOnly ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <Package className="w-3.5 h-3.5" /> Ma collection ({owned.size})
          </button>
          {activeFilters && (
            <button data-testid="clear-filters" onClick={clearAll} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground hover:text-red-400 hover:border-red-400/50 transition-colors">
              <X className="w-3.5 h-3.5" /> Réinitialiser
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {WORK_ORDER.map((k) => {
            const Icon = WORK_ICONS[k];
            const active = works.includes(k);
            const c = WORK_COLORS[k];
            return (
              <button key={k} data-testid={`work-filter-${k}`} onClick={() => toggleWork(k)} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition-colors" style={{ color: active ? "#0b0f19" : c, backgroundColor: active ? c : `${c}14`, borderColor: `${c}66` }}>
                <Icon className="w-3.5 h-3.5" /> {WORK_META[k]}
              </button>
            );
          })}
        </div>

        {works.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
            <div className="inline-flex rounded-lg border border-border bg-black/20 p-0.5">
              <button data-testid="work-match-any" onClick={() => setMatchAll(false)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${!matchAll ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Au moins une</button>
              <button data-testid="work-match-all" onClick={() => setMatchAll(true)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${matchAll ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Toutes</button>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">Niveau min</span>
              <input data-testid="work-min-level" type="range" min="1" max="4" value={minLevel} onChange={(e) => setMinLevel(parseInt(e.target.value))} className="flex-1 max-w-[180px] accent-[#38BDF8] cursor-pointer" />
              <span className="font-mono text-primary text-sm w-6">≥{minLevel}</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="pal-grid">
        {list.map((p) => {
          const color = (ELEMENT_META[p.elements?.[0]] || {}).color || "#00CCFF";
          const work = p.d?.work || {};
          const workKeys = WORK_ORDER.filter((k) => work[k]);
          const isOwned = owned.has(p.i);
          return (
            <button key={p.i} data-testid={`pal-card-${p.i}`} onClick={() => openPal(p.i)} className="group text-left relative rounded-lg border border-border bg-[hsl(var(--surface))] p-4 transition-transform duration-200 hover:-translate-y-1 overflow-hidden" style={{ borderColor: `${color}33` }}>
              <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: color }} />
              {isOwned && <span data-testid={`owned-badge-${p.i}`} className="absolute left-2 top-2 z-10 inline-flex items-center gap-0.5 rounded-full bg-primary/20 border border-primary/50 text-primary px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide"><Package className="w-2.5 h-2.5" /> Possédé</span>}
              <div className="flex items-center justify-between mb-3 relative">
                <span className="font-mono text-xs text-muted-foreground">N°{p.dex >= 10000 ? "?" : p.dex}</span>
                <span className="inline-flex items-center gap-0.5 text-amber-400">
                  {Array.from({ length: starsOf(p.rarity) }).map((_, i) => <Star key={i} className="w-2.5 h-2.5 fill-current" />)}
                </span>
              </div>
              <div className="flex justify-center mb-3">
                <PalAvatar pal={p} size={64} showElement />
              </div>
              <div className="font-head font-bold text-sm mb-2 truncate text-center">{p.fr}</div>
              <div className="flex flex-wrap gap-1 justify-center mb-2">
                <ElementBadges elements={p.elements} />
              </div>
              {workKeys.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center" data-testid={`pal-work-${p.i}`}>
                  {workKeys.map((k) => {
                    const Icon = WORK_ICONS[k];
                    const c = WORK_COLORS[k];
                    const hi = works.includes(k) && work[k] >= minLevel;
                    return (
                      <span key={k} title={`${WORK_META[k]} · Nv ${work[k]}`} className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-bold" style={{ color: c, background: `${c}1a`, border: `1px solid ${c}${hi ? "" : "33"}`, boxShadow: hi ? `0 0 6px ${c}88` : "none" }}>
                        <Icon className="w-2.5 h-2.5" />{work[k]}
                      </span>
                    );
                  })}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {list.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12" data-testid="pal-empty">
          {collectionOnly ? "Aucun Pal de ta collection ne correspond à ces filtres. Ajoute des Pals dans « Ma collection » (page Breeding)." : "Aucun Pal ne correspond à ta recherche."}
        </div>
      )}
    </div>
  );
}
